import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * A component that handles title truncation with responsive behavior
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} [text] - The text element to truncate (optional)
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  /** @type {number | null} */
  #frame = null;

  /** @type {number | null} */
  #appliedLines = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.#initializeTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.#scheduleTruncation);

      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', this.#scheduleTruncation);
    }

    this.#scheduleTruncation();
  }

  /**
   * Queues a truncation pass on the next frame, collapsing bursts into one write.
   *
   * Applying the clamp resizes the element we observe, so writing synchronously
   * from the ResizeObserver callback feeds straight back into it and the browser
   * reports "ResizeObserver loop completed with undelivered notifications".
   *
   * @returns {void}
   */
  #scheduleTruncation = () => {
    if (this.#frame != null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#calculateTruncation();
    });
  };

  /**
   * Calculate truncation for the title
   *
   * @returns {void}
   */
  #calculateTruncation() {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return;

    const computedStyle = window.getComputedStyle(this);
    const lineHeight = parseFloat(computedStyle.lineHeight);

    // `line-height: normal` parses to NaN. There is nothing to clamp against,
    // and a NaN line count would never compare equal to the previous one, so the
    // observer would keep rescheduling forever.
    if (!Number.isFinite(lineHeight) || lineHeight <= 0) return;

    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    const availableHeight = this.clientHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    // Once the line count settles, stop writing so the observer goes quiet.
    if (maxLines === this.#appliedLines) return;

    this.#appliedLines = maxLines;

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  /**
   * Cancels any pending truncation pass and tears down the resize listeners.
   *
   * @returns {void}
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#frame != null) {
      cancelAnimationFrame(this.#frame);
      this.#frame = null;
    }

    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#scheduleTruncation);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;
